/* import __COLOCATED_TEMPLATE__ from './pricing-five.hbs'; */
/* RESPONSIBLE TEAM: team-pricing-and-packaging */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import PricingMetric from 'embercom/lib/purchase/pricing-metric';
import type Charge from 'embercom/models/billing/price/charge';
import type Breakdown from 'embercom/models/billing/price/breakdown';
import type IntlService from 'embercom/services/intl';
import type Price from 'embercom/models/price';
import type Product from 'embercom/models/product';
import type QuoteService from 'embercom/services/quote-service';
import { PLAN_DATA, PRICING_5_X_CORE_ESSENTIAL_ID } from 'embercom/lib/billing';
import { formatPriceValue } from 'embercom/lib/billing/format-price';

interface TooltipRow {
  label: string;
  value: string;
  originalValue?: string;
  isTotal?: boolean;
  class?: string;
}

interface Args {
  price: Price;
  product: Product;
}

export default class PricingFive extends Component<Args> {
  @service declare intl: IntlService;
  @service declare customerService: any;
  @service declare appService: AppService;
  @service declare quoteService: QuoteService;

  get billingPeriodDurationInMonths() {
    return this.customerService.customer.subscription.billingPeriodDurationInMonths;
  }

  get hasLiteSeats() {
    return (PLAN_DATA[this.planId].liteSeatLimit ?? 0) > 0;
  }

  get planName() {
    return this.args.price.breakdown.firstObject?.name;
  }

  get planId() {
    return this.args.price.breakdown.firstObject?.plan_id;
  }

  get isAddOn() {
    return this.args.product.addon;
  }

  get planBreakdowns(): Breakdown[] {
    return this.args.price.breakdown.filter(
      (bd: Breakdown) => bd.plan_id !== undefined && !bd.isSharedBaseBreakdown,
    );
  }

  p5PricingTooltipSeatsOnly(
    metric: PricingMetric,
    planAmountInCents: number,
    planPreDiscountAmountInCents?: number,
  ) {
    let label = this.intl?.t('paywalls.pricing-tooltip.pricing-five.core-seats', {
      usage: metric.usage,
      cost: formatPriceValue(metric.perUnitPrice / 100),
    });

    // Show strikethrough on pre-discount price when a discount applies
    if (
      planPreDiscountAmountInCents !== undefined &&
      planPreDiscountAmountInCents > planAmountInCents
    ) {
      return {
        label,
        value: `$${formatPriceValue(planAmountInCents / 100)}`,
        originalValue: `$${formatPriceValue(planPreDiscountAmountInCents / 100)}`,
      };
    }

    return { label, value: `$${formatPriceValue(planAmountInCents / 100)}` };
  }

  breakdownRow(bd: Breakdown): TooltipRow {
    let amountInCents = Number(bd.amount);
    let discountAmount = bd.totalChargeDiscount;
    let discountedAmountInCents = amountInCents - discountAmount;

    if (discountAmount > 0) {
      return {
        label: bd.nameWithProduct ?? bd.name ?? '',
        value: `$${formatPriceValue(discountedAmountInCents / 100)}`,
        originalValue: `$${formatPriceValue(amountInCents / 100)}`,
      };
    }

    return {
      label: bd.nameWithProduct ?? bd.name ?? '',
      value: `$${formatPriceValue(amountInCents / 100)}`,
    };
  }

  get priceTooltipRows() {
    let allBreakdowns = this.planBreakdowns;
    let firstBreakdown = allBreakdowns[0];
    if (!firstBreakdown) {
      return { planName: '', rows: [] };
    }

    let corePlanAmountInCents = Number(firstBreakdown.amount);
    let corePlanChargeDiscount = firstBreakdown.totalChargeDiscount;
    let planAmountInCents = corePlanAmountInCents - corePlanChargeDiscount;
    let planPreDiscountAmountInCents: number | undefined;

    if (this.customerService.isSelfServeAnnualCustomer) {
      let undiscountedMonthly = this.args.price.getMonthlyTotalAfterTrial(
        this.billingPeriodDurationInMonths,
        false,
      );
      if (undiscountedMonthly) {
        planPreDiscountAmountInCents = undiscountedMonthly;
      }
    } else if (corePlanChargeDiscount > 0) {
      planPreDiscountAmountInCents = corePlanAmountInCents;
    }

    let chargesArray: Charge[] = firstBreakdown.charges.toArray();

    // Hide all metrics other than core seats for P5 core plans
    if (!this.isAddOn) {
      chargesArray = chargesArray.filter(
        (charge: Charge) => charge.pricing_metric === 'core_seat_count',
      );
    }

    // Use normal pricing metric tooltipSummary for add on. We need to render included usage
    // Use p5PricingTooltipSeatsOnly to show only prices of seats
    let summaryRows = chargesArray
      .map((charge: Charge) => {
        if (this.isAddOn) {
          return new PricingMetric(charge, this.intl).tooltipSummary();
        } else {
          return this.p5PricingTooltipSeatsOnly(
            new PricingMetric(charge, this.intl),
            planAmountInCents,
            planPreDiscountAmountInCents,
          );
        }
      })
      .flat() as TooltipRow[];

    if (this.isAddOn) {
      summaryRows.unshift({
        label: this.intl.t('paywalls.pricing-tooltip.pricing-five.addon.price'),
        value: `$${formatPriceValue(this.args.price.planStartingPrice / 100)}`,
      });
    }

    if (this.hasLiteSeats) {
      summaryRows.push({
        label: this.intl?.t('paywalls.pricing-tooltip.pricing-five.lite-seats', {
          usage: this.appService.app.humanAdminsWithLiteSeat.length || 0,
        }),
        value: this.intl.t('paywalls.pricing-tooltip.pricing-five.free'),
      });
    }

    //@ts-ignore
    // downgrading only exists on the ImmutableProduct model
    // this should be set as a tracked property in paywall but it requires a bit more refactoring
    if (this.args.product.downgrading && this.planId === Number(PRICING_5_X_CORE_ESSENTIAL_ID)) {
      summaryRows.push({
        label: this.intl.t('paywalls.pricing-tooltip.pricing-five.lite-seats-note'),
        value: '',
        class: 'italic text-sm text-muted',
      });
    }

    // Build rows for additional plan breakdowns (addons) using breakdown-level discount data
    let addonBreakdowns = allBreakdowns.slice(1);
    let addonRows: TooltipRow[] = [];
    addonRows = addonBreakdowns.map((bd: Breakdown) => this.breakdownRow(bd));

    let totalAmountInCents = allBreakdowns.reduce(
      (sum: number, bd: Breakdown) => sum + Number(bd.amount) - bd.totalChargeDiscount,
      0,
    );
    let totalPreDiscountInCents = allBreakdowns.reduce(
      (sum: number, bd: Breakdown) => sum + Number(bd.amount),
      0,
    );

    let totalRow: TooltipRow = {
      label: this.intl.t('paywalls.pricing-tooltip.pricing-five.total'),
      value: `$${formatPriceValue(totalAmountInCents / 100)}`,
      isTotal: true,
    };
    if (totalPreDiscountInCents > totalAmountInCents) {
      totalRow.originalValue = `$${formatPriceValue(totalPreDiscountInCents / 100)}`;
    }
    addonRows.push(totalRow);

    return {
      planName: this.displayPlanName(this.planName as string),
      rows: [...summaryRows, ...addonRows],
    };
  }

  displayPlanName(planName: string) {
    switch (planName) {
      case 'Pricing 5.x Core Advanced':
        return this.intl.t('paywalls.pricing-tooltip.pricing-five.plan-name.advanced');
      case 'Pricing 5.x Core Expert':
        return this.intl.t('paywalls.pricing-tooltip.pricing-five.plan-name.expert');
      case 'Pricing 5.x Core Essential':
        return this.intl.t('paywalls.pricing-tooltip.pricing-five.plan-name.essential');
      default:
        return planName;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Paywalls::PricingTooltip::PricingFive': typeof PricingFive;
    'paywalls/pricing-tooltip/pricing-five': typeof PricingFive;
  }
}
